import { useMemo, useState } from "react";
import { lastDayOfMonth } from "date-fns";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Iconify from "../iconify/Iconify";
import { getMemberStatusConfig } from "../../utils/memberStatus";
import { calculateTermination } from "../../utils/terminationUtils";
import { getDateFromPayedTillString } from "../form/fields/inputFields";
import {
  useReactivateMemberSelfMutation,
  useTerminateMemberSelfMutation,
} from "../../features/members/membersApiSlice";

const formatDateInputLocal = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatMmYyyyFromDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${mm}/${date.getFullYear()}`;
};

const parseLocalDateInput = (value) => {
  if (!value || typeof value !== "string") return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const parsed = new Date(year, month, day, 12, 0, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isDateInPast = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
  const selected = new Date(date);
  selected.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return selected.getTime() < today.getTime();
};

const buildTerminationPreview = (memberSource, selectedDate) => {
  const termDate = parseLocalDateInput(selectedDate);
  if (!termDate || isDateInPast(termDate)) return null;

  let lastMonth;
  let usedFallback = false;

  const hasContractRules =
    memberSource?.contractDate &&
    Number.isFinite(Number(memberSource?.contractDuration)) &&
    Number.isFinite(Number(memberSource?.contractExtension)) &&
    Number.isFinite(Number(memberSource?.noticePeriod));

  if (hasContractRules) {
    try {
      lastMonth = calculateTermination({
        contractDate: new Date(memberSource.contractDate),
        contractDuration: Number(memberSource.contractDuration),
        contractExtension: Number(memberSource.contractExtension),
        noticePeriod: Number(memberSource.noticePeriod),
        terminationDate: termDate,
      });
    } catch {
      usedFallback = true;
    }
  } else {
    usedFallback = true;
  }

  if (!lastMonth) {
    lastMonth = formatMmYyyyFromDate(termDate);
    usedFallback = true;
  }

  const endDate = lastDayOfMonth(getDateFromPayedTillString(lastMonth));
  if (Number.isNaN(endDate.getTime())) return null;

  return {
    lastMonth,
    contractEndDate: endDate.toISOString(),
    usedFallback,
  };
};

/**
 * Confirmation dialog for a Mitglied (contract owner) to self-terminate
 * a member within their own contract.
 *
 * Props:
 *   member        – the contract member object to be terminated (limited fields from hasMembers)
 *   primaryMember – the full primary-member document (used for contract-period calculation)
 *   onClose       – called when the dialog should close without side-effects
 *   onSuccess     – called after a successful termination (falls back to onClose)
 */
export default function SelfTerminateMemberDialog({
  member,
  primaryMember,
  mode,
  onClose,
  onSuccess,
}) {
  const [step, setStep] = useState("confirm"); // "confirm" | "result"
  const [error, setError] = useState(null);
  const [resultPayload, setResultPayload] = useState(null);
  const [terminationDate, setTerminationDate] = useState(() =>
    formatDateInputLocal(new Date()),
  );

  const isReactivation =
    mode === "reactivate" ||
    (mode !== "terminate" && member?.status === "terminated");

  const [terminateMemberSelf, terminateState] = useTerminateMemberSelfMutation();
  const [reactivateMemberSelf, reactivateState] =
    useReactivateMemberSelfMutation();
  const isLoading = terminateState.isLoading || reactivateState.isLoading;

  const statusCfg = getMemberStatusConfig(member?.status);
  const minTerminationDate = useMemo(
    () => formatDateInputLocal(new Date()),
    [],
  );
  const isPastTerminationDate = useMemo(() => {
    if (isReactivation) return false;
    const parsed = parseLocalDateInput(terminationDate);
    return parsed ? isDateInPast(parsed) : false;
  }, [isReactivation, terminationDate]);

  const computed = useMemo(() => {
    if (isReactivation) return null;
    if (isPastTerminationDate) return null;
    return buildTerminationPreview(primaryMember, terminationDate);
  }, [primaryMember, terminationDate, isReactivation, isPastTerminationDate]);

  const handleConfirm = async () => {
    try {
      if (!member?._id) {
        setError("Mitglied konnte nicht ermittelt werden.");
        setStep("result");
        return;
      }

      if (isReactivation) {
        const response = await reactivateMemberSelf({
          memberId: member._id,
        }).unwrap();
        setResultPayload(response || null);
      } else {
        if (isPastTerminationDate) {
          setError(
            "Das Kündigungsdatum darf nicht in der Vergangenheit liegen.",
          );
          setStep("result");
          return;
        }
        if (!computed) {
          setError("Kündigungsdaten konnten nicht berechnet werden.");
          setStep("result");
          return;
        }
        const response = await terminateMemberSelf({
          memberId: member._id,
          terminationDate,
          lastMonth: computed.lastMonth,
          contractEndDate: computed.contractEndDate,
        }).unwrap();
        setResultPayload({
          ...(response || {}),
          lastMonth: computed.lastMonth,
          contractEndDate: computed.contractEndDate,
        });
      }

      setError(null);
      setStep("result");
    } catch (err) {
      setError(
        err?.data?.message ||
          (isReactivation
            ? "Fehler beim Reaktivieren."
            : "Fehler beim Kündigen."),
      );
      setStep("result");
    }
  };

  const handleDialogClose = (event, reason) => {
    if (isLoading) return;
    if (step === "result" && !error) {
      (onSuccess ?? onClose)?.({
        action: isReactivation ? "reactivate" : "terminate",
        memberId: member?._id,
        payedTill: resultPayload?.payedTill || null,
        lastMonth: resultPayload?.lastMonth || computed?.lastMonth || null,
        contractEndDate:
          resultPayload?.contractEndDate || computed?.contractEndDate || null,
      });
    } else {
      onClose?.();
    }
  };

  const successLastMonth =
    resultPayload?.terminationInformation?.lastMonth ||
    resultPayload?.lastMonth ||
    computed?.lastMonth ||
    "";
  const successContractEndDate =
    resultPayload?.terminationInformation?.contractEndDate ||
    resultPayload?.contractEndDate ||
    computed?.contractEndDate ||
    "";

  return (
    <Dialog open fullWidth maxWidth="xs" onClose={handleDialogClose}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Iconify
            icon={
              isReactivation
                ? "mdi:account-reactivate-outline"
                : "mdi:file-document-remove-outline"
            }
            width={22}
          />
          <Typography variant="h6" fontWeight={700}>
            {isReactivation ? "Mitglied reaktivieren" : "Mitglied kündigen"}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5 }}>
        {step === "confirm" && (
          <Stack spacing={2}>
            {isReactivation ? (
              <Alert severity="info">
                Soll{" "}
                <strong>
                  {member?.firstName} {member?.lastName}
                </strong>{" "}
                wirklich reaktiviert werden?
              </Alert>
            ) : (
              <Alert severity="warning">
                Soll{" "}
                <strong>
                  {member?.firstName} {member?.lastName}
                </strong>{" "}
                wirklich gekündigt werden?
              </Alert>
            )}

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Aktueller Status:
              </Typography>
              <Chip
                label={statusCfg.label}
                color={statusCfg.color}
                size="small"
                icon={<Iconify icon={statusCfg.icon} width={14} />}
                sx={{ fontWeight: 600 }}
              />
            </Stack>

            {!isReactivation && (
              <>
                <TextField
                  label="Kündigung zum"
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: minTerminationDate }}
                  value={terminationDate}
                  onChange={(e) => setTerminationDate(e.target.value)}
                />

                {isPastTerminationDate ? (
                  <Alert severity="error">
                    Das Kündigungsdatum darf nicht in der Vergangenheit liegen.
                  </Alert>
                ) : computed ? (
                  <>
                    <Divider />
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 0.75, display: "block" }}
                      >
                        Berechnetes Vertragsende
                      </Typography>
                      <Stack spacing={0.5}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Letzter Beitragsmonat
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="warning.dark"
                          >
                            {computed.lastMonth}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Vertragsende
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {new Date(computed.contractEndDate).toLocaleDateString(
                              "de-DE",
                            )}
                          </Typography>
                        </Stack>
                      </Stack>
                      {computed.usedFallback && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: "block" }}
                        >
                          Hinweis: Es wurden Standardwerte verwendet.
                        </Typography>
                      )}
                    </Box>
                  </>
                ) : (
                  <Alert severity="warning">
                    Bitte ein gültiges Kündigungsdatum auswählen.
                  </Alert>
                )}
              </>
            )}

            {isReactivation && (
              <>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Bei Reaktivierung wird bezahlt bis auf den aktuellen Monat
                    gesetzt.
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ mt: 0.5, color: "success.dark" }}
                  >
                    Bezahlt bis: {formatMmYyyyFromDate(new Date())}
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        )}

        {step === "result" && !error && (
          <Alert severity="success">
            <strong>
              {member?.firstName} {member?.lastName}
            </strong>{" "}
            {isReactivation
              ? "wurde erfolgreich reaktiviert."
              : "wurde erfolgreich gekündigt."}
            {isReactivation && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Bezahlt bis:{" "}
                  <strong>
                    {resultPayload?.payedTill || formatMmYyyyFromDate(new Date())}
                  </strong>
                </Typography>
              </Box>
            )}
            {!isReactivation && successLastMonth && successContractEndDate && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Letzter Beitragsmonat:{" "}
                  <strong>{successLastMonth}</strong>
                </Typography>
                <Typography variant="body2">
                  Vertragsende:{" "}
                  <strong>
                    {new Date(successContractEndDate).toLocaleDateString(
                      "de-DE",
                    )}
                  </strong>
                </Typography>
              </Box>
            )}
          </Alert>
        )}

        {step === "result" && error && (
          <Alert severity="error">{error}</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.25 }}>
        {step === "confirm" && (
          <>
            <Button color="inherit" onClick={onClose} disabled={isLoading}>
              Abbrechen
            </Button>
            <Button
              variant="contained"
              color={isReactivation ? "success" : "error"}
              onClick={handleConfirm}
              disabled={
                isLoading ||
                (!isReactivation && (!computed || isPastTerminationDate))
              }
              startIcon={
                isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Iconify
                    icon={
                      isReactivation
                        ? "mdi:account-reactivate-outline"
                        : "mdi:close-circle-outline"
                    }
                    width={18}
                  />
                )
              }
            >
              {isReactivation
                ? "Reaktivierung bestätigen"
                : "Kündigung bestätigen"}
            </Button>
          </>
        )}

        {step === "result" && error && (
          <>
            <Button color="inherit" onClick={() => setStep("confirm")}>
              Zurück
            </Button>
            <Button variant="outlined" onClick={onClose}>
              Schließen
            </Button>
          </>
        )}

        {step === "result" && !error && (
          <Button
            variant="contained"
            color={isReactivation ? "success" : "primary"}
            onClick={handleDialogClose}
          >
            Schließen
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
